import { useEffect } from 'react';
import { Tag } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import { dateFormatToLocale } from '../../../../helpers/dateTime.helper';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { TableService } from '../../../../service/table.service';
import { accountStatusFilters, getAccountStatusColor } from '../../admin-clients/admin-client.prop';
import { compareDates } from '../program-dashboard.helper';
import type { NewClient } from './modalDashboardSlice';
import { handleModalTableChange } from './modalDashboardSlice';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export const GetNewClientsModalProps = () => {
  const dispatch = useAppDispatch();
  const { getColumnSearchProps, clearSearch } = TableService();
  const productFilter = useAppSelector((state) => state.tableSlice.productFilter);
  const sortedInfo = useAppSelector((state) => state.modalDashboardSlice.requestModalParam.sortedInfo);
  const filteredInfo = useAppSelector((state) => state.modalDashboardSlice.requestModalParam.filteredInfo);

  useEffect(() => {
    dispatch(
      handleModalTableChange({
        sorter: {
          columnKey: 'full_name',
          field: 'full_name',
          order: 'ascend',
        },
      }),
    );
  }, [dispatch]);

  const columns: ColumnsType<NewClient> = [
    {
      title: 'Client name',
      dataIndex: 'full_name',
      key: 'full_name',
      width: 200,
      sorter: (a, b) => a.full_name.localeCompare(b.full_name),
      onFilter: (value, record) => record.full_name.toLowerCase().includes((value as string).toLowerCase()),
      ...getColumnSearchProps('full_name', filteredInfo.full_name),
      sortOrder: sortedInfo.field === 'full_name' ? sortedInfo.order : null,
      filteredValue: filteredInfo.full_name || null,
      className: 'ph-no-capture',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      sorter: (a, b) => a.email.localeCompare(b.email),
      ...getColumnSearchProps('email', filteredInfo.email),
      onFilter: (value, record) => record.email.toLowerCase().includes((value as string).toLowerCase()),
      sortOrder: sortedInfo.field === 'email' ? sortedInfo.order : null,
      filteredValue: filteredInfo.email || null,
      className: 'ph-no-capture',
    },
    {
      title: 'Sites',
      dataIndex: 'sites',
      key: 'sites',
      width: 100,
      ...getColumnSearchProps('sites', filteredInfo.sites),
      onFilter: (value, record) => record.sites?.some((s) => s.toLowerCase().includes((value as string).toLowerCase())),
      render: (_text, record) =>
        record.sites?.map((site, index) => {
          return (
            <div key={index}>
              {site !== null ? (
                <Tag color="geekblue" style={{ border: 'none' }}>
                  {site}
                </Tag>
              ) : null}
            </div>
          );
        }),
      sortOrder: sortedInfo.field === 'sites' ? sortedInfo.order : null,
      filteredValue: filteredInfo.sites || null,
    },
    {
      title: 'Products',
      dataIndex: 'products',
      key: 'products',
      filters: productFilter,
      onFilter: (value, record) => record.products?.includes(value as string),
      filteredValue: filteredInfo.products || null,
      width: 100,
      render: (_text, record) =>
        record.products?.map((product, index) => {
          return (
            <div key={index}>
              {product !== null ? (
                <Tag color="geekblue" style={{ border: 'none' }}>
                  {product}
                </Tag>
              ) : null}
            </div>
          );
        }),
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      width: 150,
      sorter: (a, b) => compareDates(dayjs(a.created), dayjs(b.created)),
      ...getColumnSearchProps('created', filteredInfo.created),
      onFilter: (_value, record) => {
        const [startDate, endDate] = filteredInfo.created ?? [];
        if (startDate && !endDate) {
          return dayjs(record.created).isSameOrAfter(dayjs(startDate));
        } else if (!startDate && endDate) {
          return dayjs(record.created).isSameOrBefore(dayjs(endDate));
        } else if (startDate && endDate) {
          return (
            dayjs(record.created).isSameOrAfter(dayjs(startDate)) &&
            dayjs(record.created).isSameOrBefore(dayjs(endDate))
          );
        } else {
          return false;
        }
      },
      sortOrder: sortedInfo.field === 'created' ? sortedInfo.order : null,
      filteredValue: filteredInfo.created || null,
      render: (_text, record) => (record.created ? dateFormatToLocale(new Date(record.created)) : null),
    },
    {
      title: 'Account status',
      dataIndex: 'account_status',
      key: 'account_status',
      width: 100,
      filters: accountStatusFilters,
      onFilter: (value, record) => record.account_status === value,
      render: (account_status) => (
        <Tag color={getAccountStatusColor(account_status)} style={{ border: 'none', textTransform: 'capitalize' }}>
          {account_status}
        </Tag>
      ),
      filteredValue: filteredInfo.account_status || null,
    },
  ];

  return { columns, clearSearch };
};
